import { cn } from '@/lib/utils'
import LinkWithTransition from '@/v2/components/helpers/link-with-transition'
import ResponsiveImage from '@/v2/components/helpers/responsive-image'
import { Button } from '@/v2/components/ui/button'
import SectionWrapper from '@/v2/components/wrappers/section-wrapper'
import { ImageType, LinkType } from '@/v2/types'
import { motion } from 'framer-motion'

interface ShowcaseSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  subTitle?: string
  title: string
  description?: string
  image: ImageType
  link?: LinkType
  ctaButton?: { href: string; text: string }
  imgClassName?: string
  isBgDark?: boolean
}

const ShowcaseSection: React.FC<ShowcaseSectionProps> = props => {
  const { title, description, subTitle, image, ctaButton, className, imgClassName, isBgDark } = props

  const cardBackground = isBgDark ? 'bg-[#fff]' : 'bg-[#F9F8F8]'

  return (
    <motion.section
      initial='hidden'
      whileInView='show'
      viewport={{ once: true, amount: 0.25 }}
      className={cn('landing-pages-wr section-spacing', cardBackground, className)}
    >
      <SectionWrapper className='xl-min:px-5'>
        <motion.article
          className={cn('flex items-center md-mn:gap-5 xl-min:gap-20 lg-mn:gap-12 gap-6 md-mn:flex-row flex-col')}
        >
          <div>
            <ResponsiveImage
              src={image.src}
              alt={image.alt}
              className={cn(
                'xl-min:!w-[680px] xl-min:!h-[458px] lg-mn:w-[420px] lg-mn:h-[250px] w-[350px] h-[196px]',
                imgClassName
              )}
              imgClassName={cn(
                'xl-min:!w-[680px] xl-min:!h-[458px] lg-mn:w-[420px] lg-mn:h-[250px] w-[350px] h-[196px]',
                imgClassName
              )}
            />
          </div>

          <div className='xl-min:max-w-[38%] [&>h2]:font-bold xl-min:[&>h2]:text-[32px] [&>h2]:text-xl [&>h2]:leading-[30px] xl-min:[&>h2]:leading-[56px]'>
            {subTitle && (
              <span className='text-heading-home font-bold xl-min:text-[24px] text-[18px] xl-min:mb-6 mb-2 block'>
                {subTitle}
              </span>
            )}
            <h2>{title}</h2>
            <p className='text-paragraph mt-3 leading-8 text-base xl-min:text-lg'>{description}</p>
            {/* CTA Button */}
            {ctaButton && (
              <LinkWithTransition href={ctaButton.href} className='flex justify-start mx-auto md-mn:mx-0 w-fit'>
                <Button
                  variant='call_to_action'
                  className={cn(
                    'transition-all animate-bounce hover:animated-paused mt-6 md-mn:w-[208px] xl-min:h-[50px] w-[276px] h-[44px]'
                  )}
                >
                  {ctaButton.text}
                </Button>
              </LinkWithTransition>
            )}
          </div>
        </motion.article>
      </SectionWrapper>
    </motion.section>
  )
}

export default ShowcaseSection
